'use client';

import { Building2, Users, TrendingUp, ArrowRight, Star, Target, Sparkles, Zap, Shield, Home, Calendar, CreditCard } from 'lucide-react';
import { motion } from 'framer-motion';

export default function GroupCompanies() {
    const companies = [
        {
            id: 2,
            name: '18Bricks Investment Advisory',
            tagline: 'Strategic Investment Solutions',
            description: 'Expert investment guidance for real estate portfolios, market analysis, and wealth building strategies.',
            industry: 'Investment Advisory',
            founded: '2018',
            employees: '50+',
            rating: '4.8',
            achievements: ['Portfolio Management', 'Market Research', 'ROI Analysis', 'Risk Assessment'],
            logo: '📈',
            color: 'primary',
            icon: TrendingUp,
            stats: { clients: '2000+', investments: '500+', rating: '4.8' },
            image: '/images/18brickscompanies/18brickinverstment.png'
        },
        {
            id: 3,
            name: '18Bricks Property Services',
            tagline: 'Complete Property Management',
            description: 'End-to-end property management services including maintenance, tenant management, and facility services.',
            industry: 'Property Management',
            founded: '2018',
            employees: '100+',
            rating: '4.7',
            achievements: ['Maintenance', 'Tenant Management', 'Facility Services', '24/7 Support'],
            logo: '🏠',
            color: 'secondary',
            icon: Home,
            stats: { properties: '1500+', tenants: '3000+', rating: '4.7' },
            image: '/images/18brickscompanies/18brickspartner.jpg'
        },
        {
            id: 4,
            name: '18Bricks Real Estate',
            tagline: 'Buy Rent Sell',
            description: 'Premium holiday home rentals with concierge services and exclusive amenities for unforgettable stays.',
            industry: 'Holiday Rentals',
            founded: '2018',
            employees: '80+',
            rating: '4.9',
            achievements: ['Luxury Rentals', 'Concierge Service', 'Exclusive Amenities', '24/7 Support'],
            logo: '🏖️',
            color: 'primary',
            icon: Calendar,
            stats: { properties: '500+', guests: '10000+', rating: '4.9' },
            image: '/images/18brickscompanies/group-friends-eating-restaurant.jpg'
        },
        {
            id: 5,
            name: '18Bricks Mortgage Management',
            tagline: 'Financial Solutions',
            description: 'Comprehensive mortgage services including loan processing, refinancing, and financial advisory.',
            industry: 'Financial Services',
            founded: '2018',
            employees: '60+',
            rating: '4.8',
            achievements: ['Loan Processing', 'Refinancing', 'Financial Advisory', 'Rate Comparison'],
            logo: '💳',
            color: 'secondary',
            icon: CreditCard,
            stats: { clients: '3000+', loans: '800+', rating: '4.8' },
            image: '/images/18brickscompanies/people-office-analyzing-checking-finance-graphs.jpg'
        }
    ];

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2
            }
        }
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 50, scale: 0.9 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.6,
                ease: "easeOut" as const
            }
        }
    };

    return (
        <section className="py-24 relative overflow-hidden">
            {/* Animated Background Elements */}
            {/* <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-40 -left-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute -top-20 -right-20 w-60 h-60 bg-secondary/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
                <div className="absolute -bottom-40 left-1/4 w-72 h-72 bg-primary/5 rounded-full blur-3xl animate-pulse delay-2000"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-secondary/5 rounded-full blur-3xl animate-pulse delay-3000"></div>
            </div> */}

            <div className="container mx-auto px-4 relative z-10">
                {/* Creative Header */}
                <motion.div 
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="text-center mb-20"
                >
                    <div className="inline-flex items-center gap-3 bg-secondary text-white px-4 py-1 rounded-full text-sm font-medium mb-8 border border-primary/30">
                        <Sparkles className="w-5 h-5" />
                        <span className="font-semibold">18Bricks Group Companies</span>
                        {/* <Zap className="w-5 h-5" /> */}
                    </div>
                    
                    <h2 className="text-3xl lg:text-4xl font-semibold text-gray-900 mb-6 leading-tight">
                        <span className="text-primary">
                            Building
                        </span> Success Stories
                    </h2>
                    
                    <p className="text-base text-gray-600 max-w-4xl mx-auto leading-relaxed">
                        From investment advisory to holiday homes, our comprehensive portfolio of 18Bricks companies 
                        creates synergies that drive innovation and growth across all real estate and financial sectors.
                    </p>
                </motion.div>

                {/* Image-Based Company Cards */}
                <motion.div 
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20"
                >
                    {companies.map((company, index) => {
                        const isPrimary = company.color === 'primary';
                        
                        return (
                            <motion.div
                                key={index}
                                variants={cardVariants}
                                className="group relative"
                            >
                                {/* Main Card with Image */}
                                <div className="relative bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-100">
                                    {/* Company Image */}
                                    <div className="relative h-64 overflow-hidden">
                                        <img 
                                            src={company.image} 
                                            alt={company.name}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                                        
                                        {/* Company Logo Overlay */}
                                        {/* <div className="absolute top-6 right-6 w-16 h-16 bg-white/90 backdrop-blur-sm rounded-2xl flex items-center justify-center text-2xl shadow-lg">
                                            {company.logo}
                                        </div>
                                         */}
                                        {/* Company Name Overlay */}
                                        <div className="absolute bottom-6 left-6 right-6">
                                            <h3 className="text-2xl font-bold text-white mb-2">{company.name}</h3>
                                            <p className="text-white/90 font-medium">{company.tagline}</p>
                                        </div>
                                    </div>
                                    
                                    {/* Content Section */}
                                    <div className="p-8">
                                        {/* Industry Badge */}
                                        <div className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-medium mb-4">
                                            <div className={`w-2 h-2 rounded-full ${isPrimary ? 'bg-primary' : 'bg-secondary'}`}></div>
                                            {company.industry}
                                        </div>

                                        {/* Description */}
                                        <p className="text-gray-600 mb-6 leading-relaxed">{company.description}</p>

                                        {/* Key Stats */}
                                        <div className="grid grid-cols-3 gap-4 mb-6">
                                            {Object.entries(company.stats).slice(0, 3).map(([key, value], statIndex) => (
                                                <motion.div
                                                    key={statIndex}
                                                    whileHover={{ scale: 1.05 }}
                                                    className="text-center p-3 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl hover:from-gray-100 hover:to-gray-200 transition-all duration-300 cursor-pointer"
                                                >
                                                    <div className="text-lg font-bold text-gray-900">{value}</div>
                                                    <div className="text-xs text-gray-500 capitalize">{key}</div>
                                                </motion.div>
                                            ))}
                                        </div>

                                        {/* Key Features */}
                                        <div className="mb-6">
                                            <h4 className="text-sm font-semibold text-gray-700 mb-3">Key Features:</h4>
                                            <div className="flex flex-wrap gap-2">
                                                {company.achievements.slice(0, 3).map((achievement, achievementIndex) => (
                                                    <span 
                                                        key={achievementIndex}
                                                        className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium"
                                                    >
                                                        {achievement}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Rating and CTA */}
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <div className="flex">
                                                    {[...Array(5)].map((_, i) => (
                                                        <Star 
                                                            key={i} 
                                                            className={`w-4 h-4 ${i < Math.floor(parseFloat(company.rating)) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
                                                        />
                                                    ))}
                                                </div>
                                                <span className="text-sm font-semibold text-gray-700">{company.rating}</span>
                                            </div>
                                            
                                            <motion.button
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                                className={`flex items-center gap-2 font-semibold transition-all duration-300`}
                                            >
                                                <span>Learn More</span>
                                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                            </motion.button>
                                        </div>
                                    </div>
                                </div>

                                {/* Hover Glow Effect */}
                                <div className={`absolute inset-0 rounded-3xl bg-gradient-to-r ${
                                    isPrimary ? 'from-primary/20 to-primary/5' : 'from-secondary/20 to-secondary/5'
                                } opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl -z-10`}></div>
                            </motion.div>
                        );
                    })}
                </motion.div>

                {/* Creative Group Overview */}
                {/* <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="relative"
                >
                    <div className="bg-gradient-to-r from-primary/10 via-white to-secondary/10 rounded-3xl p-16 text-center relative overflow-hidden border border-primary/20"> */}
                        {/* Floating Elements */}
                        {/* <div className="absolute top-8 left-8 w-16 h-16 bg-primary/20 rounded-full blur-xl animate-bounce"></div>
                        <div className="absolute bottom-8 right-8 w-20 h-20 bg-secondary/20 rounded-full blur-xl animate-bounce delay-1000"></div>
                        
                        <div className="relative z-10 max-w-5xl mx-auto">
                            <div className="inline-flex items-center gap-3 bg-white/80 backdrop-blur-sm px-6 py-3 rounded-full text-sm font-medium mb-8 border border-primary/20">
                                <Target className="w-5 h-5 text-primary" />
                                <span className="text-gray-700 font-semibold">Group Power</span>
                                <Shield className="w-5 h-5 text-secondary" />
                            </div>
                            
                            <h3 className="text-5xl font-bold text-gray-900 mb-8">
                                <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                                    Combined Excellence
                                </span>
                            </h3>
                            
                            <p className="text-xl text-gray-600 mb-16 leading-relaxed max-w-3xl mx-auto">
                                Together, our 18Bricks companies create a powerful ecosystem that leverages cross-industry expertise, 
                                shared resources, and collaborative innovation in real estate and financial services.
                            </p> */}
                            
                            {/* Animated Stats Grid */}
                            {/* <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                                {[
                                    { icon: Building2, value: "4", label: "Companies", color: "primary" },
                                    { icon: Users, value: "290+", label: "Employees", color: "secondary" },
                                    { icon: TrendingUp, value: "7000+", label: "Clients", color: "primary" },
                                    { icon: Home, value: "2000+", label: "Properties", color: "secondary" }
                                ].map((stat, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, scale: 0.5 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        transition={{ delay: index * 0.1, duration: 0.5 }}
                                        whileHover={{ scale: 1.1 }}
                                        className="group cursor-pointer"
                                    >
                                        <div className={`w-20 h-20 mx-auto mb-4 rounded-2xl ${
                                            stat.color === 'primary' ? 'bg-primary/20' : 'bg-secondary/20'
                                        } flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                                            <stat.icon className={`w-10 h-10 ${
                                                stat.color === 'primary' ? 'text-primary' : 'text-secondary'
                                            }`} />
                                        </div>
                                        <div className="text-3xl font-black text-gray-900 mb-2">{stat.value}</div>
                                        <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </div>
                </motion.div> */}
            </div>
        </section>
    );
}
